import { Link } from 'react-router-dom';
import { Server, Network, Wrench, ArrowRight, type LucideIcon } from 'lucide-react';
import Section from '../ui/Section';
import SectionTitle from '../ui/SectionTitle';
import { Stagger, StaggerItem } from '../ui/Reveal';

interface Pillar {
  icon: LucideIcon;
  title: string;
  description: string;
  to: string;
}

const pillars: Pillar[] = [
  {
    icon: Server,
    title: 'Servers & Workstations',
    description: 'High-performance compute from HP, Dell, Lenovo, and Supermicro — built to spec.',
    to: '/products',
  },
  {
    icon: Network,
    title: 'Network Infrastructure',
    description: 'Secure, high-speed connectivity with Cisco, Arista, and Juniper.',
    to: '/products',
  },
  {
    icon: Wrench,
    title: 'Professional Services',
    description: 'Consulting, integration, deployment, and 24/7 managed support.',
    to: '/services',
  },
];

const OverviewSection = () => {
  return (
    <Section className="bg-surface">
      <SectionTitle
        eyebrow="What We Do"
        title="Infrastructure, end to end"
        subtitle="From the right hardware to hands-on support — one accountable partner."
      />

      <Stagger className="grid gap-8 md:grid-cols-3">
        {pillars.map((pillar) => {
          const Icon = pillar.icon;
          return (
            <StaggerItem key={pillar.title} className="h-full">
              <Link
                to={pillar.to}
                className="group gradient-ring relative flex h-full flex-col rounded-2xl border border-line bg-card p-7 shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-card-hover"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 text-white shadow-lg shadow-brand-600/25">
                  <Icon className="h-7 w-7" aria-hidden="true" />
                </span>
                <h3 className="mt-5 text-xl font-bold text-content">{pillar.title}</h3>
                <p className="mt-2 flex-1 leading-relaxed text-content-muted">{pillar.description}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-semibold text-brand-700 dark:text-brand-300">
                  Learn more
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </span>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </Section>
  );
};

export default OverviewSection;
